import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  SectionHeader,
} from '@pixlide/ui';
import { ApiError } from '@/api/client';
import {
  useCreatePlatformAdmin,
  useDeleteUser,
  usePlatformAdmins,
  usePromotePlatformAdmin,
} from '@/api/queries/platform-admin';
import { createPlatformAdminSchema, type CreatePlatformAdminInput } from '@/features/team/schemas';
import { SUPER_ADMIN_PERMISSION, useAuthStore, useHasPermission } from '@/stores/auth-store';

type PlatformAdmin = NonNullable<ReturnType<typeof usePlatformAdmins>['data']>['items'][number];

/** Code métier renvoyé par l'API quand l'adresse porte déjà un compte Pixlide. */
const EMAIL_ALREADY_USED = 'auth.email_already_used';

const EMPTY_VALUES: CreatePlatformAdminInput = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
};

/**
 * Comptes back-office (`PlatformAdmin`) : liste de l'équipe Pixlide déjà en place, et
 * formulaire pour en créer un nouveau — le seul chemin pour attribuer ce rôle, le domaine
 * refusant volontairement `CreateMember`/`Invitation` pour lui (voir le plan).
 *
 * Étendre ou réduire l'équipe est réservé aux super admins : les autres admins voient la
 * liste, sans le formulaire ni la suppression.
 */
export function TeamPage() {
  const admins = usePlatformAdmins();
  const isSuperAdmin = useHasPermission(SUPER_ADMIN_PERMISSION);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <SectionHeader
        title="Équipe"
        description="Comptes back-office de l'équipe Pixlide, avec accès complet à cette interface."
      />

      {isSuperAdmin ? (
        <CreateAdminForm />
      ) : (
        <p className="rounded-xl bg-ink-50/80 px-4 py-3 text-sm text-ink-500">
          Seul un super administrateur peut ajouter ou supprimer des comptes back-office.
        </p>
      )}

      {admins.isPending ? <LoadingState label="Chargement de l'équipe…" /> : null}

      {admins.isError ? (
        <ErrorState
          message="Impossible de charger la liste des comptes back-office."
          onRetry={() => void admins.refetch()}
        />
      ) : null}

      {admins.data?.items.length === 0 ? (
        <EmptyState
          icon={<Users className="size-10" strokeWidth={1.25} />}
          title="Aucun compte back-office"
          description="Aucun compte de l'équipe Pixlide n'est encore enregistré."
        />
      ) : null}

      {admins.data && admins.data.items.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Liste des comptes back-office de la plateforme</caption>

              <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
                <tr>
                  <th scope="col" className="px-5 py-3">Nom</th>
                  <th scope="col" className="px-5 py-3">Email</th>
                  <th scope="col" className="px-5 py-3">Créé le</th>
                  {isSuperAdmin ? (
                    <th scope="col" className="px-5 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>

              <tbody className="divide-y divide-ink-100/70">
                {admins.data.items.map((admin) => (
                  <AdminRow key={admin.id} admin={admin} canDelete={isSuperAdmin} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Une ligne de l'équipe. La suppression passe par une confirmation en deux temps, comme sur
 * la liste des utilisateurs ; elle n'est pas proposée sur un super admin ni sur son propre
 * compte, que l'API refuse de toute façon.
 */
function AdminRow({ admin, canDelete }: { admin: PlatformAdmin; canDelete: boolean }) {
  const deleteUser = useDeleteUser();
  const currentUserId = useAuthStore((state) => state.user?.userId);
  const [isConfirming, setIsConfirming] = useState(false);

  const isDeletable = canDelete && !admin.isSuperAdmin && admin.id !== currentUserId;
  const deleteError = deleteUser.error instanceof ApiError ? deleteUser.error : null;

  return (
    <tr>
      <th scope="row" className="px-5 py-4 text-left font-medium">
        <span className="flex flex-wrap items-center gap-1.5">
          {admin.firstName} {admin.lastName}
          {admin.isSuperAdmin ? <Badge tone="brand">Super admin</Badge> : null}
        </span>
      </th>

      <td className="px-5 py-4 text-ink-500">{admin.email}</td>

      <td className="px-5 py-4 text-ink-500">
        {new Date(admin.createdAt).toLocaleDateString('fr-FR')}
      </td>

      {canDelete ? (
        <td className="px-5 py-4">
          {!isDeletable ? null : isConfirming ? (
            <div className="flex flex-col items-end gap-2">
              <p className="max-w-xs text-right text-xs text-ink-500">
                Supprimer ce compte back-office ? Ses sessions seront fermées immédiatement.
              </p>

              <div className="flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={deleteUser.isPending}
                  onClick={() => {
                    deleteUser.mutate({ scope: 'team', userId: admin.id });
                    setIsConfirming(false);
                  }}
                >
                  Confirmer
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsConfirming(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-end gap-1.5">
              <Button
                variant="danger"
                size="sm"
                disabled={deleteUser.isPending}
                aria-label={`Supprimer le compte back-office de ${admin.email}`}
                onClick={() => setIsConfirming(true)}
              >
                <Trash2 aria-hidden className="size-4" />
              </Button>

              {deleteError === null ? null : (
                <p role="alert" className="max-w-xs text-right text-xs text-danger-500">
                  {deleteError.message}
                </p>
              )}
            </div>
          )}
        </td>
      ) : null}
    </tr>
  );
}

/**
 * Formulaire de création d'un compte back-office, avec réinitialisation après succès.
 *
 * Une adresse email ne porte qu'UN seul compte sur toute la plateforme (index unique en base,
 * et la connexion se fait par email seul) : quand l'adresse est déjà prise, la création est
 * refusée en 409 et aucun second compte n'est possible. Le formulaire propose alors la seule
 * issue existante — promouvoir le compte déjà là — plutôt que de laisser l'utilisateur devant
 * une impasse.
 */
function CreateAdminForm() {
  const createAdmin = useCreatePlatformAdmin();
  const promoteAdmin = usePromotePlatformAdmin();

  // Mémorisée au moment du refus, et non relue dans le champ : l'adresse promue doit rester
  // celle qui a été refusée, même si le champ est modifié entre-temps.
  const [conflictEmail, setConflictEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreatePlatformAdminInput>({
    resolver: zodResolver(createPlatformAdminSchema),
    defaultValues: EMPTY_VALUES,
  });

  const onSubmit = handleSubmit(async (values) => {
    setConflictEmail(null);

    try {
      await createAdmin.mutateAsync(values);
      reset(EMPTY_VALUES);
    } catch (submitError) {
      if (!(submitError instanceof ApiError)) {
        return;
      }

      if (submitError.code === EMAIL_ALREADY_USED) {
        setConflictEmail(values.email);

        return;
      }

      if (submitError.hasFieldErrors) {
        for (const [field, messages] of Object.entries(submitError.errors ?? {})) {
          if (field in EMPTY_VALUES && messages[0] !== undefined) {
            setError(field as keyof CreatePlatformAdminInput, { type: 'server', message: messages[0] });
          }
        }
      }
    }
  });

  const onPromote = async () => {
    if (conflictEmail === null) {
      return;
    }

    try {
      await promoteAdmin.mutateAsync({ email: conflictEmail });
      reset(EMPTY_VALUES);
      setConflictEmail(null);
    } catch {
      // Message rendu depuis `promoteAdmin.error` ci-dessous.
    }
  };

  const apiError = createAdmin.error instanceof ApiError ? createAdmin.error : null;

  // Le conflit d'adresse a son propre bloc, avec l'action qui le résout : le sortir de
  // l'alerte générique évite d'afficher deux fois la même chose.
  const alertMessage =
    apiError !== null && !apiError.hasFieldErrors && apiError.code !== EMAIL_ALREADY_USED
      ? apiError.message
      : null;

  const promoteError = promoteAdmin.error instanceof ApiError ? promoteAdmin.error : null;

  return (
    <Card className="flex flex-col gap-4 p-6">
      <h2 className="text-sm font-semibold text-ink-700">Nouveau compte back-office</h2>

      {alertMessage ? (
        <p role="alert" className="rounded-xl bg-danger-500/10 px-4 py-3 text-sm text-danger-500">
          {alertMessage}
        </p>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Prénom"
            autoComplete="given-name"
            required
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <Field
            label="Nom"
            autoComplete="family-name"
            required
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </div>

        <Field
          label="Adresse email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          label="Mot de passe"
          type="password"
          revealable
          autoComplete="new-password"
          required
          hint="Au moins 12 caractères."
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" isLoading={isSubmitting || createAdmin.isPending} className="mt-2 w-fit">
          <UserPlus aria-hidden className="size-4" />
          Créer le compte
        </Button>
      </form>

      {conflictEmail === null ? null : (
        <div className="flex flex-col gap-3 rounded-xl bg-ink-50/80 px-4 py-3 text-sm">
          <p role="alert" className="text-ink-700">
            Un compte Pixlide existe déjà avec <strong className="font-medium">{conflictEmail}</strong>. Une
            adresse ne peut porter qu'un seul compte : le promouvoir lui donne l'accès complet au
            back-office, le détache de son établissement d'origine et ferme ses sessions en cours. Son
            mot de passe actuel reste inchangé.
          </p>

          {promoteError === null ? null : (
            <p role="alert" className="text-danger-500">
              {promoteError.message}
            </p>
          )}

          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-fit"
            isLoading={promoteAdmin.isPending}
            onClick={() => void onPromote()}
          >
            <ShieldCheck aria-hidden className="size-4" />
            Promouvoir ce compte
          </Button>
        </div>
      )}
    </Card>
  );
}
